/** Service Plan manifest: table board, creator dialog, Manage page, Work, Visits, History, Jobs, Compliance, and Activities. */
import { ActivityEntityName, EntityViewDisplay, ServicePlan, SurfaceType } from '@jms/model';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { PlannedWorkCol } from '../shared/planned.work.board.columns';
import { servicePlanStatusTag } from './presentation/service.plan.status.colour';
import { navigateToServicePlanManage } from './services/service.plan.board.open';

export const servicePlanManifest = registerManifest<ServicePlan>({
  entity: ActivityEntityName.ServicePlan,
  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    defaults: {
      header: {
        title: 'Service Plans',
        showSearch: true,
        showPaginator: true,
      },
      itemInteraction: {
        navigate: navigateToServicePlanManage,
      },
      table: {
        fillerColumn: true,
        columns: {
          byField: {
            name: PlannedWorkCol.title,
            customer: PlannedWorkCol.customer,
            site: PlannedWorkCol.site,
            dateStart: PlannedWorkCol.date,
            dateEnd: PlannedWorkCol.date,
            status: PlannedWorkCol.status,
          },
        },
      },
    },
  },
  routes: {
    root: '/crm/service-plans',
  },
  presentation: {
    tags: {
      status: servicePlanStatusTag,
    },
  },
  surfaces: {
    [SurfaceType.Creator]: () => import('./surface/creator/service.plan.creator.surface').then(module => module.servicePlanCreatorSurface),
    [SurfaceType.Manage]: () => import('./surface/manage/service.plan.manage.surface').then(module => module.servicePlanManageSurface),
  },
  operations: {
    // A new plan is empty until its lines are authored, so landing back on the board would make the
    // very next step a hunt for the row that was just created.
    // Identity rail (320) plus one body column. A bounded height gives the Creator host a definite
    // body size to scroll while its footer keeps Cancel/Create visible.
    create: { surface: SurfaceType.Creator, navigateOnCreate: true, presentation: { mode: 'dialog', width: 'min(960px, calc(100vw - 48px))', height: 'min(800px, calc(100vh - 48px))', maxHeight: 'calc(100vh - 48px)' } },
    edit: { surface: SurfaceType.Manage },
  },
  relations: {
    [ActivityEntityName.ServiceLine]: () => import('./relations/service.plan.service.lines.relation').then(module => module.servicePlanServiceLinesBoardBuilder),
    [ActivityEntityName.Visit]: () => import('./relations/service.plan.visits.relation').then(module => module.servicePlanVisitsBoardBuilder),
    [ActivityEntityName.WorkTaskSchedule]: () => import('../work.task.schedule/relations/work.task.schedule.relation').then(module => module.servicePlanWorkTaskScheduleRelation),
    [ActivityEntityName.Job]: () => import('./relations/service.plan.jobs.relation').then(module => module.servicePlanJobsBoardBuilder),
    [ActivityEntityName.ComplianceFlag]: () => import('./relations/service.plan.compliance.relation').then(module => module.servicePlanComplianceBoardBuilder),
    [ActivityEntityName.Activity]: () => import('./relations/service.plan.activities.relation').then(module => module.servicePlanActivitiesRelationBuilder),
  },
});
